import { styleToString } from '@tools/ControllerTools';
import type { Action } from 'svelte/action';

class State<T> {
	public activeIndexes: number[] = $state([]);
	public multiple = $state(false);

	public items: AccordionItem<T>[] = $state([]);

	constructor(private readonly options: AccordionOpions<T>) {
		this.multiple = this.options.multiple;
	}

	public toggle(index: number) {
		if (this.activeIndexes.includes(index)) {
			this.activeIndexes = this.activeIndexes.filter((i) => i !== index);
		} else {
			if (!this.multiple && this.activeIndexes.length > 0) {
				this.activeIndexes = [];
			}
			this.activeIndexes.push(index);
		}
	}

	public goIn(item: AccordionItem<T>) {
		this.options.goIn(item);
	}
	public goOut(item: AccordionItem<T>) {
		this.options.goOut(item);
	}
}

export class AccordionItem<T> {
	public readonly data: T;
	private readonly state: State<T>;
	private readonly index: number = 0;

	public ratio: number = $state(0);
	private totalHeight: number = $state(0);
	public isActive: boolean = $derived.by(() => {
		return this.state.activeIndexes.includes(this.index);
	});

	// Round the current height to avoid jumping multiline text
	// TODO: Have round as a option
	public currentHeight = $derived(Math.round(this.ratio * this.totalHeight));

	constructor(data: T, index: number, state: State<T>) {
		this.data = data;
		this.state = state;
		this.index = index;

		$effect(() => {
			if (this.isActive) {
				this.state.goIn(this);
			} else {
				this.state.goOut(this);
			}
		});
	}

	public toggle = () => {
		this.state.toggle(this.index);
	};

	public open = () => {
		if (!this.isActive) this.state.toggle(this.index);
	};

	public close = () => {
		if (this.isActive) this.state.toggle(this.index);
	};

	public get attrTrigger(): Record<string, string> {
		return {
			'aria-expanded': this.isActive ? 'true' : 'false',
			style: styleToString({
				cursor: 'pointer'
			})
		};
	}

	public get attrContent(): Record<string, string> {
		return {
			style: styleToString({
				overflow: 'hidden',
				display: this.ratio > 0 ? null : 'none',
				height: this.ratio === 1 ? 'auto' : `${this.currentHeight}px`
			})
		};
	}

	public useTrigger: Action<HTMLElement> = (node: HTMLElement) => {
		const onClick = (event: MouseEvent) => {
			event.preventDefault();
			this.state.toggle(this.index);
		};

		node.addEventListener('click', onClick);
		return {
			destroy: () => {
				node.removeEventListener('click', onClick);
			}
		};
	};

	private readContentHeight(node: HTMLElement) {
		const savedStyle: string | null = node.getAttribute('style');

		// we can olnly read the height of the content when it is visible
		node.style.display = 'block';
		node.style.height = 'auto';
		const height = node.offsetHeight;

		// Restore the original style
		if (savedStyle !== null) node.setAttribute('style', savedStyle);
		return height;
	}

	public useContent: Action<HTMLElement> = (node: HTMLElement) => {
		this.totalHeight = this.readContentHeight(node);

		const onResize = () => {
			this.totalHeight = this.readContentHeight(node);
		};
		window.addEventListener('resize', onResize);
		return {
			destroy: () => {
				window.removeEventListener('resize', onResize);
			}
		};
	};
}

export interface AccordionOpions<T> {
	multiple: boolean;
	goIn: (item: AccordionItem<T>) => void;
	goOut: (item: AccordionItem<T>) => void;
}

export class Accordion<T> {
	public readonly state: Omit<State<T>, 'goIn' | 'goOut'>;

	private readonly options: AccordionOpions<T>;

	public get items() {
		return this.state.items;
	}

	public static from<T>(data: T[], options: Partial<AccordionOpions<T>> = {}) {
		const accordion = new Accordion<T>(options);
		data.forEach((item) => {
			accordion.add(item);
		});
		return accordion;
	}

	constructor(options: Partial<AccordionOpions<T>> = {}) {
		this.options = {
			multiple: false,
			goIn: (item: AccordionItem<T>) => {
				item.ratio = 1;
			},
			goOut: (item: AccordionItem<T>) => {
				item.ratio = 0;
			},
			...options
		};

		this.state = new State(this.options);
	}

	public add(data: T) {
		const item = new AccordionItem(data, this.state.items.length, this.state as State<T>);
		this.state.items.push(item);
		return item;
	}

	public getItem(index: number) {
		return this.state.items[index];
	}
}
